import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const AIStructureReview = ({ 
  sections, 
  onSectionsChange, 
  onConfirm, 
  onCancel,
  isLoading = false 
}) => {
  const [editableSections, setEditableSections] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newSectionTitle, setNewSectionTitle] = useState("");
  const [newSectionDescription, setNewSectionDescription] = useState("");

  useEffect(() => {
    // Add unique IDs to sections for drag and drop
    const sectionsWithIds = (sections || []).map((section, index) => ({
      ...section,
      id: section.id || `section-${index}`,
    }));
    setEditableSections(sectionsWithIds);
  }, [sections]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(editableSections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order numbers
    const reorderedItems = items.map((item, index) => ({
      ...item,
      suggestedOrder: index + 1
    }));

    setEditableSections(reorderedItems);
    onSectionsChange(reorderedItems);
  };

  const handleEditSection = (index) => {
    setEditingIndex(index);
  };

  const handleSaveEdit = (index, updatedSection) => {
    const updated = [...editableSections];
    updated[index] = { ...updated[index], ...updatedSection };
    setEditableSections(updated);
    onSectionsChange(updated);
    setEditingIndex(null);
  };

  const handleDeleteSection = (index) => {
    const updated = editableSections.filter((_, i) => i !== index);
    // Reorder remaining sections
    const reordered = updated.map((section, i) => ({
      ...section,
      suggestedOrder: i + 1
    }));
    setEditableSections(reordered);
    onSectionsChange(reordered);
  };

  const handleAddSection = () => {
    if (!newSectionTitle.trim()) return;

    const newSection = {
      id: `section-${editableSections.length}`,
      title: newSectionTitle,
      description: newSectionDescription || "Custom section added by user",
      wordCount: 500,
      priority: "Optional",
      suggestedOrder: editableSections.length + 1,
      userAdded: true
    };

    const updated = [...editableSections, newSection];
    setEditableSections(updated);
    onSectionsChange(updated);
    setNewSectionTitle("");
    setNewSectionDescription("");
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "Critical":
        return "text-red-600 bg-red-50";
      case "Important":
        return "text-yellow-600 bg-yellow-50";
      case "Optional":
        return "text-green-600 bg-green-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <FontAwesomeIcon 
          icon="spinner" 
          className="text-4xl text-blue-500 animate-spin mb-4" 
        />
        <p className="text-gray-600">AI is analyzing RFP documents...</p>
        <p className="text-sm text-gray-500 mt-2">This may take a moment</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">
          AI-Suggested Document Structure
        </h3>
        <p className="text-sm text-blue-700">
          Based on the RFP analysis, here's the recommended structure. 
          You can reorder, edit, remove, or add sections as needed.
        </p>
      </div>

      {/* Sections List with Drag and Drop */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="sections">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="space-y-2"
            >
              {editableSections.map((section, index) => (
                <Draggable
                  key={section.id}
                  draggableId={section.id}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`bg-white border rounded-lg p-4 ${
                        snapshot.isDragging ? "shadow-lg" : "shadow-sm"
                      } ${section.userAdded ? "border-blue-300" : "border-gray-200"}`}
                    >
                      {editingIndex === index ? (
                        // Edit Mode
                        <div className="space-y-3">
                          <input
                            type="text"
                            value={section.title}
                            onChange={(e) =>
                              handleSaveEdit(index, { title: e.target.value })
                            }
                            className="w-full px-3 py-2 border rounded-md"
                            placeholder="Section Title"
                          />
                          <textarea
                            value={section.description}
                            onChange={(e) =>
                              handleSaveEdit(index, { description: e.target.value })
                            }
                            className="w-full px-3 py-2 border rounded-md"
                            rows="2"
                            placeholder="Section Description"
                          />
                          <div className="flex gap-2">
                            <input
                              type="number"
                              value={section.wordCount}
                              onChange={(e) =>
                                handleSaveEdit(index, { wordCount: parseInt(e.target.value) })
                              }
                              className="w-32 px-3 py-2 border rounded-md"
                              placeholder="Word Count"
                            />
                            <select
                              value={section.priority}
                              onChange={(e) =>
                                handleSaveEdit(index, { priority: e.target.value })
                              }
                              className="px-3 py-2 border rounded-md"
                            >
                              <option value="Critical">Critical</option>
                              <option value="Important">Important</option>
                              <option value="Optional">Optional</option>
                            </select>
                            <button
                              onClick={() => setEditingIndex(null)}
                              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
                            >
                              Done
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <div
                              {...provided.dragHandleProps}
                              className="mt-1 cursor-move text-gray-400 hover:text-gray-600"
                            >
                              <FontAwesomeIcon icon="bars" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold text-gray-900">
                                  {index + 1}. {section.title}
                                </h4>
                                <span
                                  className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(
                                    section.priority
                                  )}`}
                                >
                                  {section.priority}
                                </span>
                                {section.userAdded && (
                                  <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-600">
                                    User Added
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mb-2">
                                {section.description}
                              </p>
                              <div className="flex items-center gap-4 text-xs text-gray-500">
                                <span>
                                  <FontAwesomeIcon icon="file-alt" className="mr-1" />
                                  ~{section.wordCount} words
                                </span>
                                {section.rfpReference && (
                                  <span>
                                    <FontAwesomeIcon icon="link" className="mr-1" />
                                    {section.rfpReference}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => handleEditSection(index)}
                              className="text-blue-500 hover:text-blue-700"
                              title="Edit section"
                            >
                              <FontAwesomeIcon icon="edit" />
                            </button>
                            <button
                              onClick={() => handleDeleteSection(index)}
                              className="text-red-500 hover:text-red-700"
                              title="Remove section"
                            >
                              <FontAwesomeIcon icon="trash" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Add New Section */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3">Add Custom Section</h4>
        <div className="space-y-3">
          <input
            type="text"
            value={newSectionTitle}
            onChange={(e) => setNewSectionTitle(e.target.value)}
            className="w-full px-3 py-2 border rounded-md"
            placeholder="Section Title"
          />
          <textarea
            value={newSectionDescription}
            onChange={(e) => setNewSectionDescription(e.target.value)}
            className="w-full px-3 py-2 border rounded-md"
            rows="2"
            placeholder="Section Description (optional)"
          />
          <button
            onClick={handleAddSection}
            disabled={!newSectionTitle.trim()}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300"
          >
            <FontAwesomeIcon icon="plus" className="mr-2" />
            Add Section
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center pt-4 border-t">
        <div className="text-sm text-gray-600">
          Total Sections: {editableSections.length}
        </div>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(editableSections)}
            disabled={editableSections.length === 0}
            className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-300"
          >
            Create Document with This Structure
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIStructureReview;